import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { motion } from 'framer-motion';
import React, { memo } from 'react';
import { match } from 'ts-pattern';
import { useFavoriteProducts } from '../../../hooks/useFavoriteProducts';
import { useFundingCountdown } from '../../../hooks/useFundingCountdown';
import {
  FundingDisplayMode,
  fundingDisplayModes,
  useFundingDisplay,
} from '../../../hooks/useFundingDisplay';
import { useModalState } from '../../../hooks/useModalState';
import { useProductPricesSub } from '../../../hooks/useProductPricesSub';
import { ProductType } from '../../../hooks/useProducts';
import { useProjectedFunding } from '../../../hooks/useProjectedFunding';
import { useCustomBreakpoint, useTailwindBreakpoint } from '../../../hooks/useTailwindBreakpoint';
import { cn } from '../../../lib/classNameHelpers';
import { formatDollar, formatNumber, formatPercentage } from '../../../lib/formatters';
import { getDecimalPlaces, multiply } from '../../../lib/math';
import {
  fundingRateTooltip,
  midPriceTooltip,
  nextFundingTooltip,
  openInterestTooltip,
  oraclePriceTooltip,
} from '../../../lib/tooltips';
import { Card } from '../../Card';
import { ColoredMetric } from '../../ColoredMetric/ColoredMetric';
import { FavoriteButton } from '../../FavoriteButton/FavoriteButton';
import { Kbd } from '../../Kbd/Kbd';
import { ScrollContainer } from '../../ScrollContainer/ScrollContainer';
import { SimpleSelect } from '../../SimpleSelect/SimpleSelect';
import { Skeleton } from '../../Skeleton/Skeleton';
import { Tooltip } from '../../Tooltip/Tooltip';
import { useNumberHighlight } from '../hooks/useNumberHighlight';
import { MarketsModal } from './MarketsModal';

interface TradeColProps {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

export const TradeCol: React.FC<TradeColProps> = ({ label, value, className, isLoading }) => (
  <div className={cn('flex flex-col gap-0.5 px-1', className)}>
    <span className="text-nowrap text-2xs font-medium text-contentSecondary">{label}</span>
    <div className="flex flex-wrap items-center gap-2 text-xs text-contentTertiary">
      {isLoading ? <Skeleton className="h-4 w-[70px] sm:w-20" /> : <span>{value}</span>}
    </div>
  </div>
);

const MidPrice = ({
  midPrice,
  isLoading,
  priceDecimals,
  className,
}: {
  isLoading: boolean;
  midPrice: bigint | undefined;
  priceDecimals: number;
  className?: string;
}) => {
  const highlightClassName = useNumberHighlight(midPrice);
  return (
    <div className={className}>
      {isLoading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <span
          className={cn(
            'flex items-center font-medium text-contentTertiary transition-colors',
            highlightClassName
          )}
        >
          <Tooltip content={midPriceTooltip()}>
            {formatNumber(midPrice, {
              minimumFractionDigits: priceDecimals,
              maximumFractionDigits: priceDecimals,
            })}
          </Tooltip>
        </span>
      )}
    </div>
  );
};

const MarketPickerButton = memo(
  ({
    ticker,
    productId,
    isFavorite,
    onToggleFavorite,
  }: {
    ticker: string;
    productId: string;
    isFavorite: boolean;
    onToggleFavorite: (productId: string) => void;
  }) => {
    const setMarketsModal = useModalState((state) => state.setMarketsModal);
    const marketModalOpen = useModalState((state) => state.marketsModal === 'open');
    // Handle market button and star hovering so that we prevent showing button
    // hover state when we are hovering over star.
    const [isHoveringButton, setIsHoveringButton] = React.useState(false);
    const [isHoveringStar, setIsHoveringStar] = React.useState(false);

    const handleClick = () => setMarketsModal('open');
    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Don't handle keyboard events if FavoriteButton is focused
      if (document.activeElement?.tagName === 'BUTTON') return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    return (
      // Use div that acts as button to prevent nested buttons
      <div
        role="button"
        tabIndex={0}
        aria-label="Open market selector"
        title="Open market selector"
        aria-expanded={marketModalOpen}
        aria-haspopup="dialog"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHoveringButton(true)}
        onMouseLeave={() => setIsHoveringButton(false)}
        className={cn(
          'flex h-full cursor-pointer items-center lg:gap-3 lg:px-3 lg:py-2 lg:pr-2 transition-colors',
          isHoveringButton && !isHoveringStar && 'lg:bg-backgroundHoverOverlay'
        )}
      >
        <div className="flex items-center justify-center gap-1.5">
          <FavoriteButton
            className="hidden size-5 lg:block"
            title={isFavorite ? `Unfavorite ${ticker}` : `Favorite ${ticker}`}
            isFavorite={isFavorite}
            onToggle={() => onToggleFavorite(productId)}
            onMouseEnter={() => setIsHoveringStar(true)}
            onMouseLeave={() => setIsHoveringStar(false)}
          />
          <span className="text-nowrap text-lg font-medium text-contentPrimary">{ticker}</span>
        </div>
        <div className="hidden items-center gap-1 lg:flex">
          <Kbd>{navigator.userAgent.toLocaleLowerCase().includes('mac') ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>K</Kbd>
        </div>
        <motion.div animate={{ rotate: marketModalOpen ? -180 : 0 }}>
          <ChevronDownIcon className="size-6 text-contentSecondary" />
        </motion.div>
      </div>
    );
  }
);

const Change24h = ({
  change24hrPrice,
  change24hrPercent,
  priceDecimals,
  showPrice,
}: {
  change24hrPrice: bigint | undefined;
  change24hrPercent: bigint | undefined;
  priceDecimals: number;
  showPrice?: boolean;
}) => (
  <div className="flex gap-1">
    {showPrice && (
      <ColoredMetric
        className="text-xs"
        value={change24hrPrice}
        formatFn={(x) =>
          formatNumber(x, {
            signDisplay: 'exceptZero',
            minimumFractionDigits: priceDecimals,
            maximumFractionDigits: priceDecimals,
          })
        }
      />
    )}
    <ColoredMetric
      className="text-xs"
      value={change24hrPercent}
      formatFn={(x) =>
        `${showPrice ? '(' : ''}${formatPercentage(x, {
          signDisplay: 'exceptZero',
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        })}${showPrice ? ')' : ''}`
      }
    />
  </div>
);

const FundingCountdown: React.FC<{ fundingUpdatedAt: number | undefined }> = ({
  fundingUpdatedAt,
}) => {
  const fundingCountdown = useFundingCountdown(fundingUpdatedAt);
  return fundingCountdown.formatted;
};

export const TradeHeaderUi: React.FC<{
  ticker: string;
  isLoading: boolean;
  displayTicker: string;
  productId: string;
  isFavorite: boolean;
  onToggleFavorite: (productId: string) => void;
  markPrice: bigint | undefined;
  midPrice: bigint | undefined;
  change24hrPrice: bigint | undefined;
  change24hrPercent: bigint | undefined;
  volume24h: bigint | undefined;
  openInterest: bigint | undefined;
  FundingCountdown: typeof FundingCountdown;
  fundingRate: bigint | undefined;
  tickSize: bigint;
  fundingUpdatedAt: number | undefined;
  fundingDisplayMode: FundingDisplayMode;
  setFundingDisplayMode: (mode: FundingDisplayMode) => void;
}> = ({
  isLoading,
  markPrice,
  midPrice,
  change24hrPrice,
  change24hrPercent,
  volume24h,
  openInterest,
  fundingRate,
  displayTicker,
  productId,
  isFavorite,
  onToggleFavorite,
  tickSize,
  fundingUpdatedAt,
  FundingCountdown,
  fundingDisplayMode,
  setFundingDisplayMode,
}) => {
  const priceDecimals = getDecimalPlaces(tickSize);

  const isLg = useTailwindBreakpoint('lg');
  const shouldShowFunding = useCustomBreakpoint('336px');

  return (
    <Card className="scrollbar-none w-full px-3 py-2 lg:p-0">
      <ScrollContainer className="flex gap-3 md:gap-4 lg:gap-0">
        <div className="flex shrink-0 flex-col lg:flex-row lg:items-center lg:gap-0">
          <MarketPickerButton
            ticker={displayTicker}
            productId={productId}
            isFavorite={isFavorite}
            onToggleFavorite={onToggleFavorite}
          />
          {!isLg && !isLoading && (
            <Change24h
              priceDecimals={priceDecimals}
              change24hrPrice={change24hrPrice}
              change24hrPercent={change24hrPercent}
            />
          )}
          <MidPrice
            isLoading={isLoading}
            midPrice={midPrice}
            priceDecimals={priceDecimals}
            className="lg:py-2 lg:pl-2 lg:pr-4"
          />
        </div>
        <div className="flex flex-wrap items-baseline gap-4 lg:flex-nowrap lg:py-2 lg:pr-3 2xl:gap-1 min-[1450px]:gap-4">
          <TradeCol
            isLoading={isLoading}
            label={<Tooltip content={oraclePriceTooltip()}>Oracle Price</Tooltip>}
            value={formatNumber(markPrice, {
              minimumFractionDigits: priceDecimals,
              maximumFractionDigits: priceDecimals,
            })}
          />
          {isLg && (
            <TradeCol
              isLoading={isLoading}
              label="24h Change"
              value={
                <Change24h
                  priceDecimals={priceDecimals}
                  change24hrPrice={change24hrPrice}
                  change24hrPercent={change24hrPercent}
                />
              }
            />
          )}
          <TradeCol
            isLoading={isLoading}
            label="24h Volume"
            value={formatDollar(volume24h, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          />
          <TradeCol
            className="hidden sm:flex"
            isLoading={isLoading}
            label={<Tooltip content={openInterestTooltip()}>Open Interest</Tooltip>}
            value={formatDollar(openInterest, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          />
          {shouldShowFunding && (
            <>
              <TradeCol
                isLoading={isLoading}
                label={<Tooltip content={fundingRateTooltip()}>Funding Rate</Tooltip>}
                value={
                  <div className="flex gap-1">
                    <ColoredMetric
                      value={fundingRate}
                      formatFn={(x) =>
                        formatPercentage(x, {
                          signDisplay: 'negative',
                          minimumFractionDigits: 4,
                          maximumFractionDigits: 4,
                        })
                      }
                    />
                    <SimpleSelect
                      dropdownClassName="mt-2"
                      options={fundingDisplayModes.map((mode) => ({
                        value: mode,
                        label: mode,
                      }))}
                      onChange={(x) => x?.value && setFundingDisplayMode(x.value)}
                      selected={{ value: fundingDisplayMode, label: fundingDisplayMode }}
                    />
                  </div>
                }
              />
              <TradeCol
                isLoading={isLoading}
                label={<Tooltip content={nextFundingTooltip()}>Next Funding</Tooltip>}
                value={<FundingCountdown fundingUpdatedAt={fundingUpdatedAt} />}
              />
            </>
          )}
        </div>
      </ScrollContainer>
    </Card>
  );
};

export const TradeHeader: React.FC<{ product: ProductType }> = ({ product }) => {
  const { ticker, displayTicker, openInterest } = product;
  const { data: pricesByProductId, isPending: isPricesPending } = useProductPricesSub();
  const { data: projectedFunding, isLoading: isProjectedFundingLoading } = useProjectedFunding(
    product.id
  );
  const { fundingDisplayMode, setFundingDisplayMode } = useFundingDisplay();
  const { favoriteProducts, toggleProductFavorite } = useFavoriteProducts();
  const fundingRateProjected1h = projectedFunding?.fundingRateProjected1h;
  const fundingRateProjected8h = projectedFunding?.fundingRateProjected8h;
  const fundingRateProjected1d = projectedFunding?.fundingRateProjected1d;
  const fundingRateProjected1y = projectedFunding?.fundingRateProjected1y;
  const fundingRate = match(fundingDisplayMode)
    .with('1h', () => fundingRateProjected1h)
    .with('8h', () => fundingRateProjected8h)
    .with('1d', () => fundingRateProjected1d)
    .with('1y', () => fundingRateProjected1y)
    .exhaustive();
  const { oraclePrice, midPrice, change24hrPrice, change24hrPercent } =
    pricesByProductId[product.id] || {};

  const isLoading =
    isPricesPending || Object.keys(pricesByProductId).length === 0 || isProjectedFundingLoading;

  return (
    <>
      <TradeHeaderUi
        ticker={ticker}
        isLoading={isLoading}
        displayTicker={displayTicker}
        productId={product.id}
        isFavorite={Boolean(favoriteProducts[product.id])}
        onToggleFavorite={toggleProductFavorite}
        markPrice={oraclePrice}
        midPrice={midPrice}
        change24hrPrice={change24hrPrice}
        change24hrPercent={change24hrPercent}
        volume24h={oraclePrice ? multiply(product.volume24h, oraclePrice) : undefined}
        openInterest={oraclePrice ? multiply(openInterest, oraclePrice) : undefined}
        fundingRate={fundingRate}
        tickSize={product.tickSize}
        fundingUpdatedAt={product.fundingUpdatedAt}
        FundingCountdown={FundingCountdown}
        fundingDisplayMode={fundingDisplayMode}
        setFundingDisplayMode={setFundingDisplayMode}
      />
      <MarketsModal />
    </>
  );
};
